import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// ============================================================================
//  AgingReportSub — NEW Aging Report sub-page (Chunk A)
//
//  Chunk A scope:
//    - Fetch /reports.php?action=aging_report once on mount
//    - Display 5 KPI cards (totalAged, critical, oldest, avgAge, agedOut>14d)
//    - Display 5 aging buckets with click-to-filter
//    - Display first 50 rows of criticalList in a simple table
//
//  Full polish (brand-product tree, distribution chart, cards view,
//  multi-filter sidebar, exports) lands in Chunk C.
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || '/warehouse-system/api';

const bucketByAge = (age) => {
  if (age <= 7)  return 'fresh';
  if (age <= 14) return 'normal';
  if (age <= 30) return 'warning';
  if (age <= 60) return 'alert';
  return 'critical';
};

const fmtN = (n) => Number(n || 0).toLocaleString();
const fmtK = (n) => (Number(n || 0) / 1000).toFixed(1) + 'k';

export default function AgingReportSub() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [bucketFilter, setBucketFilter] = useState('all');
  const [filterMode,   setFilterMode]   = useState('pallets'); // pallets | cartons | kgs

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Use axios so the global App.js interceptor adds the JWT Bearer token.
        // Plain fetch() doesn't go through that interceptor, which caused the
        // "No authentication token provided" backend rejection.
        const { data: json } = await axios.get(`${API_BASE}/reports.php`, {
          params: { action: 'aging_report' },
        });
        if (cancelled) return;
        if (!json?.success) throw new Error(json?.error || 'Unknown error');
        setData(json.data);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Totals (safe defaults if data not yet loaded)
  const totals = useMemo(() => {
    if (!data?.buckets) return { pallets: 0, cartons: 0, kgs: 0 };
    return data.buckets.reduce(
      (s, b) => ({
        pallets: s.pallets + (b.pallets || 0),
        cartons: s.cartons + (b.cartons || 0),
        kgs:     s.kgs     + (b.kgs     || 0),
      }),
      { pallets: 0, cartons: 0, kgs: 0 }
    );
  }, [data]);

  const filtered = useMemo(() => {
    if (!data?.criticalList) return [];
    if (bucketFilter === 'all') return data.criticalList;
    return data.criticalList.filter(p => bucketByAge(p.ageDays) === bucketFilter);
  }, [data, bucketFilter]);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div>Loading aging report…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="error-state">
        Failed to load aging report: {error}
      </div>
    );
  }
  if (!data) return null;

  const K = data.kpis || {};
  const total = filterMode === 'pallets' ? totals.pallets
              : filterMode === 'cartons' ? totals.cartons
              : totals.kgs;

  return (
    <>
      {/* ===== KPI ROW ===== */}
      <div className="kpi-row">
        <div className="kpi-card blue">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="kpi-label">Total Aged Inventory</div>
            <span style={{ fontSize: 16, opacity: 0.4 }}>📦</span>
          </div>
          <div className="kpi-value">{fmtN(K.totalAged)}</div>
          <div className="kpi-sub">
            {fmtN(totals.cartons)} ctn · {fmtK(totals.kgs)} KG
          </div>
        </div>
        <div className="kpi-card red">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="kpi-label">⚠ Critical (&gt;60d)</div>
            <span style={{ fontSize: 16, opacity: 0.4 }}>🔴</span>
          </div>
          <div className="kpi-value" style={{ color: '#dc2626' }}>{fmtN(K.criticalCount)}</div>
          <div className="kpi-sub">{fmtN(K.criticalCartons)} ctn · urgent action</div>
        </div>
        <div className="kpi-card amber">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="kpi-label">Oldest Item</div>
            <span style={{ fontSize: 16, opacity: 0.4 }}>⏱</span>
          </div>
          <div className="kpi-value">
            {fmtN(K.oldestDays)}<span style={{ fontSize: 16 }}>d</span>
          </div>
          <div className="kpi-sub" title={K.oldestLot || ''}>
            {(K.oldestBrand || '—')}
            {K.oldestLot ? ` · ${String(K.oldestLot).slice(0, 22)}…` : ''}
          </div>
        </div>
        <div className="kpi-card violet">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="kpi-label">Average Age</div>
            <span style={{ fontSize: 16, opacity: 0.4 }}>📊</span>
          </div>
          <div className="kpi-value">
            {Number(K.avgAgeDays || 0).toFixed(1)}<span style={{ fontSize: 16 }}>d</span>
          </div>
          <div className="kpi-sub">weighted by pallet count</div>
        </div>
        <div className="kpi-card purple">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="kpi-label">Out for Processing &gt;14d</div>
            <span style={{ fontSize: 16, opacity: 0.4 }}>🚚</span>
          </div>
          <div className="kpi-value" style={{ color: '#5b21b6' }}>{fmtN(K.agedOutOverdue14d)}</div>
          <div className="kpi-sub">of {fmtN(K.agedOutProcessing)} total out</div>
        </div>
      </div>

      {/* ===== AGING DISTRIBUTION BUCKETS ===== */}
      <div className="section">
        <div className="section-h">
          ⏱ Aging Distribution
          <span className="badge">{(data.buckets || []).length} buckets</span>
          <div className="section-controls">
            <div className="subtabs" style={{ marginRight: 8 }}>
              {['pallets', 'cartons', 'kgs'].map(m => (
                <button
                  key={m}
                  className={'subtab' + (filterMode === m ? ' active' : '')}
                  onClick={() => setFilterMode(m)}
                >
                  {m === 'pallets' ? '🛒 Pallets' : m === 'cartons' ? '📦 Cartons' : '⚖ KG'}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="aging-bucket-row">
          {(data.buckets || []).map(b => {
            const v   = filterMode === 'pallets' ? b.pallets
                      : filterMode === 'cartons' ? b.cartons
                      : b.kgs;
            const pct = total > 0 ? (v / total) * 100 : 0;
            return (
              <div
                key={b.id}
                className={'aging-bucket ' + (b.cls || b.id) + (bucketFilter === b.id ? ' active' : '')}
                onClick={() => setBucketFilter(bucketFilter === b.id ? 'all' : b.id)}
                title="Click to filter the list below"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div className="aging-bucket-label">{b.label}</div>
                  <div className="aging-bucket-range">{pct.toFixed(0)}%</div>
                </div>
                <div className="aging-bucket-value">
                  {filterMode === 'kgs' ? fmtK(v) : fmtN(v)}
                </div>
                <div className="aging-bucket-sub">
                  {fmtN(b.pallets)} pal · {fmtN(b.lots)} lots · {fmtK(b.kgs)} KG
                </div>
                <div className="aging-bucket-bar">
                  <div className="aging-bucket-bar-fill" style={{ width: pct + '%' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== CRITICAL LIST (top 100, simple table view in Chunk A) ===== */}
      <div className="section">
        <div className="section-h">
          🔴 Aged Inventory · Detailed List
          <span className="badge">{filtered.length} shown</span>
          <div className="section-controls">
            <button
              className={'period-btn' + (bucketFilter === 'all' ? ' active' : '')}
              onClick={() => setBucketFilter('all')}
            >
              All
            </button>
            {(data.buckets || []).map(b => (
              <button
                key={b.id}
                className={'period-btn' + (bucketFilter === b.id ? ' active' : '')}
                onClick={() => setBucketFilter(b.id)}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
            No aged items match the current filter.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Lot</th>
                  <th>Brand</th>
                  <th>Product</th>
                  <th>Grade</th>
                  <th>Pack Size</th>
                  <th className="num">Pallets</th>
                  <th className="num">Cartons</th>
                  <th className="num">KG</th>
                  <th>Age</th>
                  <th>Status</th>
                  <th>Date In</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, idx) => {
                  const cls = bucketByAge(p.ageDays);
                  return (
                    <tr key={p.lot + '|' + idx}>
                      <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, fontWeight: 600 }}>{p.lot}</td>
                      <td style={{ fontWeight: 600 }}>{p.brand}</td>
                      <td><span className="doc-link RPK">{p.product}</span></td>
                      <td className="num">{p.grade}</td>
                      <td>{p.pack}</td>
                      <td className="num">{fmtN(p.pallets)}</td>
                      <td className="num">{fmtN(p.cartons)}</td>
                      <td className="num">{fmtN(p.kgs)}</td>
                      <td><span className={'age-pill ' + cls}>{p.ageDays}d</span></td>
                      <td><span className={'status-pill ' + p.status}>{p.statusLabel}</span></td>
                      <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, color: 'var(--text-muted)' }}>{p.dateIn}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', padding: 14, color: 'var(--text-muted)', fontSize: 11.5 }}>
        Chunk A: KPIs + Buckets + List · Chunk C will add Brand-Product tree, distribution chart, card view, multi-filter, and exports.
      </div>
    </>
  );
}
